import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import axios from "axios";

const defaultSeasonType = {
  title: "",
  description: "",
  entryFee: 0,
  size: 0,
  prizeBreakdown: "",
  mvpPrize: 0,
  days: 0,
  durationSeconds: 0,
  isEnabled: true,
  seasonConfig: [],
};

export default function VersusSeasonTypeDialog(props) {
  const [seasonType, setSeasonType] = useState(null);
  const [loading, setLoading] = useState(false);

  const SECONDS_IN_DAY = 86400;

  useEffect(() => {
    setLoading(true);
    if (props?.seasonTypeId) {
      axios
        .get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/versus/season-types/${props.seasonTypeId}`
        )
        .then(function (response) {
          const data = response.data.data;
          setSeasonType({
            ...data,
            days: Math.round(data.durationSeconds / SECONDS_IN_DAY),
          });
          setLoading(false);
        })
        .catch(function (error) {
          console.error("Error fetching season type:", error);
          setLoading(false);
        });
    } else {
      setSeasonType(defaultSeasonType);
      setLoading(false);
    }
  }, [props.seasonTypeId]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setSeasonType((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const handleNumberChange = (e) => {
    const { id, value } = e.target;
    setSeasonType((prevState) => ({
      ...prevState,
      [id]: value === "" ? "" : Number(value),
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setSeasonType((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  };

  const handleSeasonConfigChange = (blockIndex, field, value) => {
    setSeasonType((prevState) => {
      const newSeasonConfig = Array.isArray(prevState.seasonConfig)
        ? [...prevState.seasonConfig]
        : [];

      while (newSeasonConfig.length <= blockIndex) {
        newSeasonConfig.push({});
      }

      newSeasonConfig[blockIndex] = {
        ...newSeasonConfig[blockIndex],
        [field]: value === "" ? "" : Number(value),
      };

      return {
        ...prevState,
        seasonConfig: newSeasonConfig,
      };
    });
  };

  const onSuccess = () => {
    setLoading(true);

    const apiSeasonType = {
      ...seasonType,
      durationSeconds: seasonType.days * SECONDS_IN_DAY,
    };

    delete apiSeasonType.days;

    if (props?.seasonTypeId) {
      axios
        .put(
          `${process.env.REACT_APP_API_BASE_URL}/admin/versus/season-types/${props.seasonTypeId}`,
          apiSeasonType
        )
        .then(function (response) {
          setLoading(false);
          props.onSuccessHandler();
        })
        .catch(function (error) {
          console.error("Error updating season type:", error);
          setLoading(false);
        });
    } else {
      axios
        .post(
          `${process.env.REACT_APP_API_BASE_URL}/admin/versus/season-types`,
          apiSeasonType
        )
        .then(function (response) {
          setLoading(false);
          props.onSuccessHandler();
        })
        .catch(function (error) {
          console.error("Error creating season type:", error);
          setLoading(false);
        });
    }
  };

  const onCancel = () => {
    props.onClose();
  };

  const isFormValid = () => {
    return (
      seasonType &&
      seasonType.title &&
      seasonType.entryFee >= 0 &&
      seasonType.size > 0 &&
      seasonType.days > 0
    );
  };

  return (
    <Dialog
      open={props.open}
      onClose={() => {}}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
    >
      <DialogTitle id="alert-dialog-title">
        {props?.seasonTypeId
          ? "Edit Versus Season Type"
          : "New Versus Season Type"}
      </DialogTitle>
      <DialogContent>
        {seasonType === null || loading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center" }}
            style={{ padding: 20 }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                id="title"
                label="Title"
                fullWidth
                value={seasonType.title}
                variant="outlined"
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                id="description"
                label="Description"
                fullWidth
                value={seasonType.description}
                variant="outlined"
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                id="entryFee"
                label="Entry Fee"
                fullWidth
                type="number"
                value={seasonType.entryFee}
                variant="outlined"
                onChange={handleNumberChange}
                required
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                id="size"
                label="Size"
                fullWidth
                type="number"
                value={seasonType.size}
                variant="outlined"
                onChange={handleNumberChange}
                required
                inputProps={{ min: 1 }}
                helperText="Number of teams in the season"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                id="days"
                label="Duration (days)"
                fullWidth
                type="number"
                value={seasonType.days}
                variant="outlined"
                onChange={handleNumberChange}
                required
                inputProps={{ min: 1 }}
                helperText="Will be converted to seconds on save"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                id="prizeBreakdown"
                label="Prize Breakdown"
                fullWidth
                value={seasonType.prizeBreakdown}
                variant="outlined"
                onChange={handleChange}
                helperText="Format: 1=1250,2=500 (position=amount)"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                id="mvpPrize"
                label="MVP Prize"
                fullWidth
                type="number"
                value={seasonType.mvpPrize}
                variant="outlined"
                onChange={handleNumberChange}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={seasonType.isEnabled}
                    onChange={handleCheckboxChange}
                    name="isEnabled"
                    color="primary"
                  />
                }
                label="Enabled"
              />
            </Grid>

            {/* Season Config Blocks */}
            {seasonType.size > 0 && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    {Array.from({ length: seasonType.size - 1 }, (_, index) => (
                      <Grid item xs={12} md={4} key={index}>
                        <Box
                          sx={{
                            mb: 2,
                            p: 2,
                            border: "1px solid #ccc",
                            borderRadius: 1,
                            position: "relative",
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              backgroundColor: "#1976d2",
                              color: "white",
                              borderRadius: "50%",
                              width: 24,
                              height: 24,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.875rem",
                              fontWeight: "bold",
                              zIndex: 1,
                            }}
                          >
                            {index + 2}
                          </Box>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField
                                label="Win Probability"
                                fullWidth
                                type="number"
                                value={
                                  seasonType.seasonConfig?.[index]
                                    ?.winProbability || ""
                                }
                                variant="outlined"
                                onChange={(e) =>
                                  handleSeasonConfigChange(
                                    index,
                                    "winProbability",
                                    e.target.value
                                  )
                                }
                                inputProps={{ min: 0, max: 100, step: 0.01 }}
                                helperText="Percentage (0-100)"
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="MVP Over"
                                fullWidth
                                type="number"
                                value={
                                  seasonType.seasonConfig?.[index]?.mvpOver ||
                                  ""
                                }
                                variant="outlined"
                                onChange={(e) =>
                                  handleSeasonConfigChange(
                                    index,
                                    "mvpOver",
                                    e.target.value
                                  )
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="MVP Under"
                                fullWidth
                                type="number"
                                value={
                                  seasonType.seasonConfig?.[index]?.mvpUnder ||
                                  ""
                                }
                                variant="outlined"
                                onChange={(e) =>
                                  handleSeasonConfigChange(
                                    index,
                                    "mvpUnder",
                                    e.target.value
                                  )
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={onSuccess}
          variant="contained"
          color="primary"
          autoFocus
          disabled={!isFormValid() || loading}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
